import { matchPath } from 'react-router-dom'

// Route patterns that decide where the New Issue dialog lives and where the
// global "Report" entry point is offered. Kept in one place so the dialog host
// (ProjectsRoot), the mobile FAB and the desktop sidebar button cannot drift
// apart. They mirror the nested paths declared in `routes.tsx`.

/** Every screen of a single project stage. */
export const STAGE_ROUTE_PATTERN = `/project_stages/:projectStageId/*`

/** Per-type issues list (the map view is the same route with `?view=MAP`). */
export const ISSUES_LIST_ROUTE_PATTERN = `/project_stages/:projectStageId/issue_types/:issueTypeId/issues`

/**
 * Single issue detail. It mounts its own related-issue `IssueDialog` and
 * `MapModal`, both driven by the same `dialogOpen` flag as the New Issue
 * dialog, so the stage-level host must stay off this route.
 */
export const ISSUE_DETAIL_ROUTE_PATTERN = `/project_stages/:projectStageId/issue_types/:issueTypeId/issues/:issueId`

export function isIssuesListRoute(pathname: string): boolean {
  return matchPath(ISSUES_LIST_ROUTE_PATTERN, pathname) !== null
}

export function isIssueDetailRoute(pathname: string): boolean {
  return matchPath(ISSUE_DETAIL_ROUTE_PATTERN, pathname) !== null
}

/** Whether the stage-level New Issue dialog host is mounted on this stage route. */
export function hostsStageNewIssueDialog(pathname: string): boolean {
  return !isIssueDetailRoute(pathname)
}

/**
 * Whether this route fixes the issue type in its URL. The issues list and the
 * issue detail derive the current type from the path (useLoadIssuesData sets
 * it from `:issueTypeId`), so picking another type there would desync the
 * screen from its data, and the URL would set it back anyway.
 */
export function bindsIssueTypeToUrl(pathname: string): boolean {
  return isIssuesListRoute(pathname) || isIssueDetailRoute(pathname)
}

/**
 * The stage id when this route offers the global "Report" entry point, else
 * null. Offered on every stage screen whose issue type is NOT bound to the URL
 * (see bindsIssueTypeToUrl).
 */
export function matchGlobalNewIssueRoute(pathname: string): string | null {
  const stageMatch = matchPath(STAGE_ROUTE_PATTERN, pathname)
  const projectStageId = stageMatch?.params.projectStageId
  if (!projectStageId) return null
  if (bindsIssueTypeToUrl(pathname)) return null
  return projectStageId
}
